import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAppointmentSlotsAction,
  getAppointmentSlotsResetAction,
  scheduleAppointmentAction,
  scheduleAppointmentResetAction,
  scheduleRecurringAppointmentAction,
  scheduleRecurringAppointmentResetAction,
  updateRecurringSeriesAction,
  updateRecurringSeriesResetAction,
  getPatientTimeSlotsAction,
  getPatientTimeSlotsResetAction,
  getRecurringAppointmentCountAction,
  getRecurringAppointmentCountResetAction,
} from 'src/redux/actions/appointmentAction';
import { resetEditTaskTimeAction } from 'src/redux/actions/editTaskTimeAction';
import {
  therapistMigrateTaskAction,
  therapistMigrateTaskResetAction,
} from 'src/redux/actions/therapistMigrateTaskAction';
import {
  updateAppointmentEndTimeAction,
  updateAppointmentEndTimeResetAction,
} from 'src/redux/actions/therapyPlanning';
import WarningModal from 'src/components/therapyPlanning_2.0/commonComponent/warning-modal';
import { getPayerCateGroup } from 'src/layouts/components/utils-method';
import useCurrentUser from 'src/hooks/use-current-user';
import { appointmentBookFlag, recurringAppointmentBookFlag } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';

interface AppointmentSlot {
  time: string;
  patient: string;
  duration: string;
  available: boolean;
  unavailable?: boolean;
  temporary?: boolean;
  isAppointment?: boolean;
  appointmentData?: any;
}

interface Props {
  open: boolean;
  onClose: () => void;
  therapists: any[];
  selectedTherapist: any | null;
  patient: any;
  localDate: string;
  onScheduleAppointment: (assignedPatientUuid?: string) => void;
  // Refresh context for proper therapist list refresh (matching drag-and-drop behavior)
  providerId?: string;
  therapistsList?: any[]; // Full therapist list for page calculation
  selectedSortType?: string;
  currentTab?: string; // discipline
  filterTherapistList?: any[];
  isDragDrop?: boolean; // Flag to indicate if opened from drag and drop
  migrateRecurring?: boolean; // Flag to indicate if this is a recurring appointment migration
}

const AppointmentSchedulingModal: React.FC<Props> = ({
  open,
  onClose,
  therapists,
  selectedTherapist,
  patient,
  localDate,
  onScheduleAppointment,
  providerId: propProviderId,
  therapistsList,
  selectedSortType,
  currentTab,
  filterTherapistList,
  isDragDrop = false,
  migrateRecurring = false,
}) => {
  const dispatch = useDispatch();
  const [providerIdFromHook] = useCurrentUser(['providerId']);
  const providerId = propProviderId || providerIdFromHook;
  const [isAppointmentBook] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');
  const [isRecurringAppointmentBook] = useFeatureToggleWhenReady(
    recurringAppointmentBookFlag,
    'FACILITY'
  );

  const isRecurringEdit = useSelector(
    (state: any) => state?.isRecurringEditReducer?.isRecurringEdit
  );

  // Helper function to calculate therapist page number (matching drag-and-drop logic)
  const getTherapistPageNumber = (therapistList: any[], therapistUuid: string): number => {
    const therapistsPerPage = 20;
    const index = therapistList?.findIndex((therapist: any) => therapist?.uuid === therapistUuid);
    if (index === -1) return 0;
    return Math.floor(index / therapistsPerPage);
  };
  const [selectedTherapistUuid, setSelectedTherapistUuid] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [numberOfMinutes, setNumberOfMinutes] = useState<number | string>(
    patient?.numberOfMinutes || 30
  );
  const lastUpdatedMinutesRef = useRef<number | string | null>(null);
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlapPayload, setOverlapPayload] = useState<{
    providerId: any;
    taskUuid: string;
    time: number | string;
  } | null>(null);
  const lastMigrateRef = useRef<{ providerId: any; taskUuid: string; time: number | string } | null>(
    null
  );
  const lastScheduleRef = useRef<any>(null);
  const lastMigratePayloadRef = useRef<any>(null);
  const [overlapSource, setOverlapSource] = useState<'MIGRATE' | 'SCHEDULE' | null>(null);
  const [showRecurringDayPopup, setShowRecurringDayPopup] = useState<boolean>(false);
  const [selectedRecurringDays, setSelectedRecurringDays] = useState<number[]>([]); // Array of weekday numbers (0=Sunday, 6=Saturday)
  const [scheduledRecurringAppointments, setScheduledRecurringAppointments] = useState<
    Array<{ weekday: number; dayName: string; time: string }>
  >([]); // Store scheduled appointments with day and time
  const [isFirstRecurringSchedule, setIsFirstRecurringSchedule] = useState<boolean>(false);
  const [isDoneSchedulingClicked, setIsDoneSchedulingClicked] = useState<boolean>(false);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(null);
  const [originalRecurrenceEndDate, setOriginalRecurrenceEndDate] = useState<Date | null>(null); // Store the original end date selected in date picker
  const [selectedRecurringWeekday, setSelectedRecurringWeekday] = useState<number | null>(null); // Track selected weekday for display (0=Sunday, 6=Saturday)

  const appointmentSlotsState = useSelector((state: any) => state?.getAppointmentSlotsReducer);
  const scheduleAppointmentState = useSelector((state: any) => state?.scheduleAppointmentReducer);
  const scheduleRecurringAppointmentState = useSelector(
    (state: any) => state?.scheduleRecurringAppointmentReducer
  );
  const updateRecurringSeriesState = useSelector(
    (state: any) => state?.updateRecurringSeriesReducer
  );
  const migrateTaskState = useSelector((state: any) => state?.therapistMigrateTaskReducer);
  const updateAppointmentEndTimeResponse = useSelector(
    (state: any) => state?.updateAppointmentEndTimeReducer
  );
  const editTaskTimeState = useSelector((state: any) => state?.editTaskTimeReducer);
  const patientTimeSlotsState = useSelector((state: any) => state?.getPatientTimeSlotsReducer);
  const recurringAppointmentCountState = useSelector(
    (state: any) => state?.getRecurringAppointmentCountReducer
  );

  const loading = appointmentSlotsState?.loading || false;
  const appointmentData = appointmentSlotsState?.response || [];
  const patientTimeSlotsData = patientTimeSlotsState?.data || [];

  // When API succeeds, ensure we're using the tracked updated value
  // Reset state after handling to clean up
  useEffect(() => {
    if (editTaskTimeState?.isTimeEdited && lastUpdatedMinutesRef.current !== null) {
      // Ensure the state reflects the successfully updated value
      setNumberOfMinutes(lastUpdatedMinutesRef.current);
      // Reset the state after handling (suppressSuccessMessage flag prevents parent from showing message)
      dispatch(resetEditTaskTimeAction());
    }
  }, [editTaskTimeState?.isTimeEdited, dispatch]);

  // Show error message only if API fails (no success message shown)
  useEffect(() => {
    // Check if there's an error: response exists, isTimeEdited is false, loading is false
    // Error condition: response exists but doesn't have data property (success has data.data)
    if (
      editTaskTimeState?.response &&
      !editTaskTimeState?.isTimeEdited &&
      !editTaskTimeState?.loading &&
      !editTaskTimeState?.data
    ) {
      const errorMessage =
        editTaskTimeState?.response?.message ||
        editTaskTimeState?.response?.error ||
        (typeof editTaskTimeState?.response === 'string' ? editTaskTimeState.response : null) ||
        'Failed to update minutes. Please try again.';

      alert(errorMessage);
      // Reset the error state after showing the message
      dispatch(resetEditTaskTimeAction());
    }
  }, [
    editTaskTimeState?.response,
    editTaskTimeState?.isTimeEdited,
    editTaskTimeState?.loading,
    editTaskTimeState?.data,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedTherapist && open) {
      setSelectedTherapistUuid(selectedTherapist.uuid);
    }
    // Only reset numberOfMinutes if modal is opening fresh
    // If we have a successfully updated value, use that instead of patient prop
    if (patient && open) {
      // If we have a tracked updated value, use it; otherwise use patient's value
      if (lastUpdatedMinutesRef.current !== null) {
        setNumberOfMinutes(lastUpdatedMinutesRef.current);
      } else {
        setNumberOfMinutes(patient?.numberOfMinutes || 30);
      }
    }
  }, [selectedTherapist, open, patient]);

  useEffect(() => {
    if (selectedTherapistUuid && open && localDate) {
      const therapist = therapists.find((t) => t.uuid === selectedTherapistUuid);
      const discipline = therapist?.discipline || selectedTherapist?.discipline || 'PT';

      dispatch(
        getAppointmentSlotsAction({
          therapistUuid: selectedTherapistUuid,
          discipline,
          localDate,
        })
      );
    }
  }, [selectedTherapistUuid, open, localDate, dispatch, therapists, selectedTherapist]);

  // Call patient time slots API when modal opens
  useEffect(() => {
    if (open && patient && localDate) {
      const patientId = patient?.patientUuid || patient?.uuid;
      if (patientId) {
        dispatch(
          getPatientTimeSlotsAction({
            patientId: patientId,
            taskDate: localDate,
          })
        );
      }
    }
    return () => {
      // Reset on unmount
      dispatch(getPatientTimeSlotsResetAction());
    };
  }, [open, patient, localDate, dispatch]);

  useEffect(() => {
    if (
      selectedTimeSlot &&
      isRecurring &&
      recurrenceEndDate &&
      selectedRecurringWeekday !== null &&
      selectedTherapistUuid &&
      localDate
    ) {
      const therapist = therapists.find((t) => t.uuid === selectedTherapistUuid);
      const discipline =
        therapist?.discipline || selectedTherapist?.discipline || currentTab || 'PT';

      // Convert weekday number to day name (0=Sunday -> SUNDAY, 4=Thursday -> THURSDAY, etc.)
      const dayNames = [
        'SUNDAY',
        'MONDAY',
        'TUESDAY',
        'WEDNESDAY',
        'THURSDAY',
        'FRIDAY',
        'SATURDAY',
      ];
      const daysOfWeek = dayNames[selectedRecurringWeekday];

      // Get today's date in YYYY-MM-DD format
      const startDate = moment().format('YYYY-MM-DD');
      // Get end date from date picker in YYYY-MM-DD format
      const endDate = moment(recurrenceEndDate).format('YYYY-MM-DD');

      // Parse the selected time and create appointment times
      const time24 = moment(selectedTimeSlot, 'h:mm A').format('HH:mm:ss');
      const duration = Number(numberOfMinutes) || 30;
      const appointmentStartTime = moment
        .utc(`${localDate} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
        .format('YYYY-MM-DDTHH:mm:ss[Z]');
      const appointmentEndTime = moment
        .utc(`${localDate} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
        .add(duration, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss[Z]');

      dispatch(
        getRecurringAppointmentCountAction({
          therapistUuid: selectedTherapistUuid,
          discipline,
          daysOfWeek,
          startDate,
          endDate,
          appointmentStartTime,
          appointmentEndTime,
        })
      );
    } else {
      dispatch(getRecurringAppointmentCountResetAction());
    }
  }, [
    selectedTimeSlot,
    isRecurring,
    recurrenceEndDate,
    selectedRecurringWeekday,
    selectedTherapistUuid,
    localDate,
    numberOfMinutes,
    therapists,
    selectedTherapist,
    currentTab,
    dispatch,
  ]);

  // Generate time slots from 7 AM to 5 PM in 15-minute intervals
  const generateTimeSlots = React.useMemo(() => {
    const slots = [];
    const startHour = 7; // 7 AM
    const endHour = 17; // 5 PM

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === endHour && minute > 45) break; // Stop at 5:45 PM

        const time24 = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}:00`;
        const timeMoment = moment(time24, 'HH:mm:ss');
        const time12 = timeMoment.format('h:mm A');

        slots.push({
          time24,
          time12,
          hour: timeMoment.format('hA'),
        });
      }
    }

    return slots;
  }, []);

  const assignedInpatients: string[] = React.useMemo(() => {
    if (!appointmentData || !Array.isArray(appointmentData)) {
      return [];
    }

    return appointmentData
      .filter((patient: any) => !patient?.appointmentStartTime)
      .map((patient: any) => {
        const duration = patient?.numberOfMinutes || '';
        const durationStr = duration ? ` (${patient?.numberOfMinutes} Min.)` : '';
        return `${patient?.patientLastname}, ${patient?.patientFirstname}${durationStr}`;
      });
  }, [appointmentData]);

  const scheduledAppointmentsMap = React.useMemo(() => {
    const map: { [key: string]: any } = {};

    if (appointmentData && Array.isArray(appointmentData)) {
      appointmentData
        .filter(
          (appt: any) => appt?.appointmentStartTime && appt?.scheduleItemType === 'APPOINTMENT'
        )
        .forEach((appt: any) => {
          // Get the discipline-specific start times array
          const selectedTherapist = therapists?.find((t) => t.uuid === selectedTherapistUuid);
          const currentDiscipline =
            selectedTherapist?.discipline || selectedTherapist?.discipline || currentTab || 'PT';

          let startTimesArray: string[] = [];
          if (
            currentDiscipline === 'PT' &&
            appt?.ptStartTimes &&
            Array.isArray(appt?.ptStartTimes)
          ) {
            startTimesArray = appt?.ptStartTimes;
          } else if (
            currentDiscipline === 'OT' &&
            appt?.otStartTimes &&
            Array.isArray(appt?.otStartTimes)
          ) {
            startTimesArray = appt?.otStartTimes;
          } else if (
            currentDiscipline === 'SLP' &&
            appt?.slpStartTimes &&
            Array.isArray(appt?.slpStartTimes)
          ) {
            startTimesArray = appt?.slpStartTimes;
          }

          // If no discipline-specific times, fall back to appointmentStartTime
          if (startTimesArray?.length === 0) {
            const appointmentTime = moment(
              appt?.appointmentStartTime,
              'YYYY-MM-DDTHH:mm:ss'
            ).format('HH:mm:ss');
            map[appointmentTime] = { ...appt, isFirstSlot: true };
          } else {
            // Only map the first slot (appointmentStartTime) - don't mark subsequent slots
            const firstTimeSlot = startTimesArray[0];
            const time24 = moment(firstTimeSlot).format('HH:mm:ss');
            map[time24] = { ...appt, isFirstSlot: true };
          }
        });
    }

    return { appointmentMap: map };
  }, [appointmentData, selectedTherapistUuid, therapists, currentTab]);

  const appointmentSlots: AppointmentSlot[] = React.useMemo(() => {
    const currentPatientUuid = patient?.patientUuid || patient?.uuid;

    return generateTimeSlots?.map((slot) => {
      // Show temporary pill when a slot is selected
      if (selectedTimeSlot === slot.time12 && patient) {
        const duration = patient?.numberOfMinutes ? `${numberOfMinutes} Min.` : '';
        return {
          time: slot.time12,
          patient: patient?.patientLastname
            ? `${patient?.patientLastname}, ${patient?.patientFirstname}`
            : patient?.name,
          duration,
          available: false,
          temporary: true,
        };
      }

      const scheduledPatient = scheduledAppointmentsMap?.appointmentMap[slot?.time24];

      if (scheduledPatient) {
        const scheduledPatientUuid = scheduledPatient?.patientUuid;
        const scheduledTaskUuid = scheduledPatient?.taskUuid;
        const currentTaskUuid = patient?.taskUuid || patient?.taskId;
        const isCurrentPatient =
          scheduledPatientUuid === currentPatientUuid && scheduledTaskUuid === currentTaskUuid;

        // Hide current patient's existing appointment when they've selected a new slot
        // This allows the temporary pill to show in the new location
        if (isCurrentPatient && selectedTimeSlot) {
          return {
            time: slot?.time12,
            patient: 'Available',
            duration: '',
            available: true,
          };
        }

        const isUnavailable =
          scheduledPatient?.status === 'unavailable' ||
          scheduledPatient?.patientFirstname === 'not available';

        if (isUnavailable) {
          return {
            time: slot.time12,
            patient: 'Patient not available',
            duration: '',
            available: false,
            unavailable: true,
          };
        }

        // Use numberOfMinutes state if this is the selected patient being edited, otherwise use API data
        const minutesToDisplay = isCurrentPatient
          ? numberOfMinutes
          : scheduledPatient?.numberOfMinutes;

        const duration = minutesToDisplay ? `${minutesToDisplay} Min.` : '';

        // Check if this is an appointment (read-only pill)
        const isAppointment = scheduledPatient?.scheduleItemType === 'APPOINTMENT';

        return {
          time: slot.time12,
          patient: `${scheduledPatient?.patientLastname}, ${scheduledPatient?.patientFirstname}`,
          duration,
          available: false,
          isAppointment: isAppointment,
          appointmentData: isAppointment ? scheduledPatient : undefined,
        };
      }

      // Check if patient is booked for other disciplines at this time
      const selectedTherapist = therapists?.find((t) => t.uuid === selectedTherapistUuid);
      const currentDiscipline =
        selectedTherapist?.discipline || selectedTherapist?.discipline || currentTab || 'PT';

      // Check patient time slots data for conflicts
      if (patientTimeSlotsData && patientTimeSlotsData?.length > 0) {
        const timeSlotsData = patientTimeSlotsData[0]; // Response is an array with one object
        const slotTime24 = slot?.time24; // Format: "HH:mm:ss"

        // Check if patient is booked for other disciplines at this time
        let isBookedForOtherDiscipline = false;

        // Helper function to check if a time matches the slot time
        const checkTimeMatch = (bookedTime: string) => {
          const bookedMoment = moment(bookedTime); // ISO format: "2025-12-11T11:30:00"
          const bookedTime24 = bookedMoment.format('HH:mm:ss');
          return bookedTime24 === slotTime24;
        };

        if (currentDiscipline === 'OT') {
          // If current discipline is OT, check PT and SLP
          if (timeSlotsData?.ptStartTimes && Array.isArray(timeSlotsData?.ptStartTimes)) {
            isBookedForOtherDiscipline = timeSlotsData?.ptStartTimes.some(checkTimeMatch);
          }
          if (
            !isBookedForOtherDiscipline &&
            timeSlotsData?.slpStartTimes &&
            Array.isArray(timeSlotsData?.slpStartTimes)
          ) {
            isBookedForOtherDiscipline = timeSlotsData?.slpStartTimes.some(checkTimeMatch);
          }
        } else if (currentDiscipline === 'PT') {
          // If current discipline is PT, check OT and SLP
          if (timeSlotsData?.otStartTimes && Array.isArray(timeSlotsData?.otStartTimes)) {
            isBookedForOtherDiscipline = timeSlotsData?.otStartTimes.some(checkTimeMatch);
          }
          if (
            !isBookedForOtherDiscipline &&
            timeSlotsData?.slpStartTimes &&
            Array.isArray(timeSlotsData?.slpStartTimes)
          ) {
            isBookedForOtherDiscipline = timeSlotsData?.slpStartTimes.some(checkTimeMatch);
          }
        } else if (currentDiscipline === 'SLP') {
          // If current discipline is SLP, check PT and OT
          if (timeSlotsData?.ptStartTimes && Array.isArray(timeSlotsData?.ptStartTimes)) {
            isBookedForOtherDiscipline = timeSlotsData?.ptStartTimes.some(checkTimeMatch);
          }
          if (
            !isBookedForOtherDiscipline &&
            timeSlotsData?.otStartTimes &&
            Array.isArray(timeSlotsData?.otStartTimes)
          ) {
            isBookedForOtherDiscipline = timeSlotsData?.otStartTimes.some(checkTimeMatch);
          }
        }

        if (isBookedForOtherDiscipline) {
          return {
            time: slot?.time12,
            patient: 'Patient not available',
            duration: '',
            available: false,
            unavailable: true,
          };
        }
      }

      return {
        time: slot?.time12,
        patient: 'Available',
        duration: '',
        available: true,
      };
    });
  }, [
    generateTimeSlots,
    scheduledAppointmentsMap,
    selectedTimeSlot,
    patient,
    numberOfMinutes,
    patientTimeSlotsData,
    selectedTherapistUuid,
    therapists,
    currentTab,
    localDate,
  ]);

  const groupedSlots = React.useMemo(() => {
    if (appointmentSlots?.length === 0) return [];

    const grouped: { [key: string]: AppointmentSlot[] } = {};

    appointmentSlots?.forEach((slot, index) => {
      const hour = moment(slot.time, 'h:mm A').format('hA'); // e.g., "7AM", "8AM"
      if (!grouped[hour]) {
        grouped[hour] = [];
      }
      grouped[hour].push(slot);
    });

    // Sort by hour and convert to array
    const sortedHours = Object.keys(grouped).sort((a, b) => {
      const hourA = moment(a, 'hA').hour();
      const hourB = moment(b, 'hA').hour();
      return hourA - hourB;
    });

    return sortedHours.map((hour) => ({
      hour,
      slots: grouped[hour],
    }));
  }, [appointmentSlots]);

  useEffect(() => {
    if (
      (scheduleAppointmentState?.success ||
        scheduleRecurringAppointmentState?.success ||
        updateRecurringSeriesState?.success ||
        migrateTaskState?.isTherapistTaskMigrated) &&
      open
    ) {
      // If popup is showing and "Done Scheduling" was not clicked, don't close - user can schedule more days
      if (showRecurringDayPopup && !isDoneSchedulingClicked) {
        // Reset the success state so we can schedule more
        dispatch(scheduleAppointmentResetAction());
        dispatch(scheduleRecurringAppointmentResetAction());
        dispatch(updateRecurringSeriesResetAction());
        dispatch(therapistMigrateTaskResetAction());
        return;
      }

      // Close the modal and refresh
      setIsDoneSchedulingClicked(false);
      // Capture patient uuid before handleCancel triggers parent onClose which
      // nulls appointmentPatient. Parent uses this to authoritatively drop the
      // just-assigned queue task from taskListState (queue auto-refresh).
      const assignedPatientUuid: string | undefined = patient?.uuid || patient?.patientUuid;
      handleCancel();

      // Trigger callback to refresh - this should trigger parent refresh logic
      // The parent component should handle the actual refresh of therapist list
      setTimeout(() => {
        onScheduleAppointment(assignedPatientUuid);
      }, 100);
    }
  }, [
    scheduleAppointmentState?.success,
    scheduleRecurringAppointmentState?.success,
    updateRecurringSeriesState?.success,
    migrateTaskState?.isTherapistTaskMigrated,
    migrateTaskState?.loading,
    open,
    showRecurringDayPopup,
    isDoneSchedulingClicked,
    dispatch,
  ]);

  // Detect OVERLAP error from migrate task or schedule appointment and open confirmation modal
  useEffect(() => {
    if (!open) return;
    const migrateOverlap =
      !migrateTaskState?.loading &&
      !migrateTaskState?.isTherapistTaskMigrated &&
      migrateTaskState?.response?.code === 'OVERLAP';
    const scheduleOverlap =
      !scheduleAppointmentState?.loading &&
      !scheduleAppointmentState?.success &&
      scheduleAppointmentState?.response?.code === 'OVERLAP';

    if (migrateOverlap || scheduleOverlap) {
      const source = migrateOverlap ? migrateTaskState : scheduleAppointmentState;
      const taskUuid =
        source?.response?.data?.taskUuid ?? lastMigrateRef.current?.taskUuid;
      const time = source?.response?.data?.time ?? lastMigrateRef.current?.time;
      const overlapProviderId = lastMigrateRef.current?.providerId ?? providerId;
      setOverlapSource(scheduleOverlap ? 'SCHEDULE' : 'MIGRATE');
      // Both flows re-dispatch the original payload with ignore-overlap=true on confirm,
      // so the modal opens whether or not the BE returns taskUuid/time in the error data.
      setOverlapPayload({ providerId: overlapProviderId, taskUuid, time });
      setOverlapModalOpen(true);
    } else if (overlapModalOpen) {
      // State cleared — close the modal
      setOverlapModalOpen(false);
      setOverlapPayload(null);
      setOverlapSource(null);
    }
  }, [migrateTaskState, scheduleAppointmentState, open]);

  // Close the modal after update-appointment-end-time succeeds
  useEffect(() => {
    if (updateAppointmentEndTimeResponse?.isSuccess) {
      setOverlapModalOpen(false);
      setOverlapPayload(null);
      dispatch(updateAppointmentEndTimeResetAction());
      dispatch(therapistMigrateTaskResetAction());
      dispatch(scheduleAppointmentResetAction());
      handleCancel();
      setTimeout(() => {
        onScheduleAppointment(patient?.uuid || patient?.patientUuid);
      }, 100);
    }
  }, [updateAppointmentEndTimeResponse?.isSuccess]);

  const handleOverlapCancel = () => {
    setOverlapModalOpen(false);
    setOverlapPayload(null);
    setOverlapSource(null);
    dispatch(therapistMigrateTaskResetAction());
    dispatch(scheduleAppointmentResetAction());
  };

  const handleOverlapConfirm = () => {
    // Schedule-appointment overlap: re-dispatch the same schedule payload with
    // ignore-overlap=true so the BE creates the appointment despite the conflict.
    if (overlapSource === 'SCHEDULE' && lastScheduleRef.current) {
      setOverlapModalOpen(false);
      setOverlapPayload(null);
      setOverlapSource(null);
      dispatch(scheduleAppointmentResetAction());
      dispatch(
        scheduleAppointmentAction({
          ...lastScheduleRef.current,
          ignoreOverlap: true,
        })
      );
      return;
    }
    // Migrate-task overlap: re-dispatch the same migrate payload with ignore-overlap=true
    // so the BE moves the task despite the conflict.
    if (overlapSource === 'MIGRATE' && lastMigratePayloadRef.current) {
      setOverlapModalOpen(false);
      setOverlapPayload(null);
      setOverlapSource(null);
      dispatch(therapistMigrateTaskResetAction());
      dispatch(
        therapistMigrateTaskAction({
          ...lastMigratePayloadRef.current,
          ignoreOverlap: true,
        })
      );
      return;
    }
    // Fallback (legacy supervisor end-time flow) — should not be reached with the
    // updated MIGRATE/SCHEDULE branches above.
    if (!overlapPayload) return;
    dispatch(
      updateAppointmentEndTimeAction({
        providerId: overlapPayload.providerId,
        taskUuid: overlapPayload.taskUuid,
        time: overlapPayload.time,
      })
    );
  };

  useEffect(() => {
    if (
      scheduleRecurringAppointmentState?.error &&
      !scheduleRecurringAppointmentState?.loading &&
      showRecurringDayPopup &&
      isDoneSchedulingClicked
    ) {
      setIsDoneSchedulingClicked(false);
      console.error('Recurring appointment error:', scheduleRecurringAppointmentState?.error);
    }
  }, [
    scheduleRecurringAppointmentState?.error,
    scheduleRecurringAppointmentState?.loading,
    showRecurringDayPopup,
    isDoneSchedulingClicked,
  ]);

  useEffect(() => {
    if (updateRecurringSeriesState?.error && !updateRecurringSeriesState?.loading) {
      console.error('Update recurring series error:', updateRecurringSeriesState?.error);
    }
  }, [updateRecurringSeriesState?.error, updateRecurringSeriesState?.loading]);

  // Reset selected time slot when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTimeSlot(null);
      // Reset the tracked updated value when modal opens fresh
      lastUpdatedMinutesRef.current = null;

      // If isRecurringEdit is true, set recurring state and date from patient
      if (isRecurringEdit) {
        setIsRecurring(true);
        // Set recurrence end date from patient?.date or patient?.recurrenceEndDate
        if (patient?.date) {
          const patientDate = moment(patient.date).toDate();
          setRecurrenceEndDate(patientDate);
          setOriginalRecurrenceEndDate(patientDate);
        } else if (patient?.recurrenceEndDate) {
          const patientEndDate = moment(patient.recurrenceEndDate).toDate();
          setRecurrenceEndDate(patientEndDate);
          setOriginalRecurrenceEndDate(patientEndDate);
        }
        // Set selected weekday from patient appointment date if available
        if (patient?.appointmentStartTime) {
          const appointmentDate = moment(patient.appointmentStartTime);
          setSelectedRecurringWeekday(appointmentDate.day());
        } else {
          setSelectedRecurringWeekday(moment().day());
        }
      } else {
        setIsRecurring(false);
        setRecurrenceEndDate(null);
        setOriginalRecurrenceEndDate(null);
        setSelectedRecurringWeekday(null);
      }

      setShowRecurringDayPopup(false);
      setSelectedRecurringDays([]);
      setScheduledRecurringAppointments([]);
      setIsFirstRecurringSchedule(true);
      setIsDoneSchedulingClicked(false);
    }
  }, [open, isRecurringEdit, patient]);

  // Set isFirstRecurringSchedule when recurring is enabled
  useEffect(() => {
    if (isRecurring && recurrenceEndDate && isFirstRecurringSchedule) {
      // Keep it as first schedule
    } else if (!isRecurring) {
      setIsFirstRecurringSchedule(true);
    }
  }, [isRecurring, recurrenceEndDate]);

  const handleTherapistChange = (event: SelectChangeEvent<string>) => {
    setSelectedTherapistUuid(event.target.value);
    setSelectedTimeSlot(null);
  };

  const handleSelectTimeSlot = (time12: string) => {
    setSelectedTimeSlot(time12);
  };

  const scheduleAppointment = () => {
    if (!selectedTherapistUuid || !selectedTimeSlot || !patient || !providerId) return;

    // Parse the selected time
    const time24 = moment(selectedTimeSlot, 'h:mm A').format('HH:mm:ss');

    // Create appointment start time in UTC - format as YYYY-MM-DDTHH:mm:ssZ
    const appointmentStartTime = moment
      .utc(`${localDate} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
      .format('YYYY-MM-DDTHH:mm:ss[Z]');

    // Calculate appointment end time based on duration - format as YYYY-MM-DDTHH:mm:ssZ
    const duration = Number(numberOfMinutes) || 30;
    const appointmentEndTime = moment
      .utc(`${localDate} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
      .add(duration, 'minutes')
      .format('YYYY-MM-DDTHH:mm:ss[Z]');

    // Detect if we're editing an existing appointment
    // Check if the patient already has an appointment by looking for them in appointmentData
    // OR check if patient object has appointmentStartTime and scheduleItemType is APPOINTMENT
    const patientUuid = patient?.patientUuid || patient?.uuid;
    const hasAppointmentStartTime = !!patient?.appointmentStartTime;
    const isAppointmentType =
      !patient?.scheduleItemType || patient?.scheduleItemType === 'APPOINTMENT';

    // Check if patient exists in appointmentData with an appointmentStartTime
    let patientExistsInAppointments = false;
    if (patientUuid && appointmentData && Array.isArray(appointmentData)) {
      patientExistsInAppointments = appointmentData?.some((appt: any) => {
        const matchesPatient = appt?.patientUuid === patientUuid || appt?.uuid === patientUuid;
        const hasAppointment = !!appt?.appointmentStartTime;
        const isAppointment = appt?.scheduleItemType === 'APPOINTMENT';
        return matchesPatient && hasAppointment && isAppointment;
      });
    }

    // Migrate path is only for tasks that already belong to a therapist:
    //   - drag-drop reassignment (isDragDrop / sourceTherapistId set), OR
    //   - editing an existing assigned appointment (patient.isAssigned === true).
    // Queue tasks (including split-Secondary) carry appointmentStartTime/APPOINTMENT
    // but no sourceTherapistId and no isAssigned flag — they must use verify, not
    // migrate, so the BE creates a new assignment instead of moving an existing one
    // (which would clobber the Main task's minutes when source === target).
    const isAlreadyAssigned = patient?.isAssigned === true || !!patient?.sourceTherapistId;
    const isEditing = isDragDrop || isAlreadyAssigned;

    // Try to find taskId from patient object (may not be needed, but we'll try to use it if available)
    let taskId = patient?.taskUuid || patient?.taskId || patient?.uuid;

    if (isEditing || isDragDrop) {
      // Use migrate endpoint when editing or when dragged and dropped
      // Source therapist is from patient object (set when dragging) or selectedTherapist
      const sourceTherapist = patient?.sourceTherapistId || selectedTherapist?.uuid;

      if (!sourceTherapist) {
        console.error('Missing source therapist for appointment migration:', {
          sourceTherapist,
          patient,
        });
        alert('Unable to edit appointment: source therapist not found.');
        return;
      }

      // Migrate endpoint uses YYYY-MM-DD format for assignDate
      const assignDate = localDate; // Already in YYYY-MM-DD format

      // Extract taskUuid from patient.appointments?.taskUuid for unprocessedData
      const patientId = patient?.patientId || patientUuid;
      let taskUuidForUnprocessedData = taskId || patientUuid;

      // Priority 1: Try to get taskUuid directly from patient.appointments
      if (
        patient?.appointments &&
        Array.isArray(patient?.appointments) &&
        patient?.appointments?.length > 0
      ) {
        const appointment =
          patient?.appointments?.find((appt: any) => appt?.taskUuid) || patient?.appointments[0];
        if (appointment?.taskUuid) {
          taskUuidForUnprocessedData = appointment?.taskUuid;
        }
      }
      // Priority 2: Try to get from patient.sourceTherapist.assignedPatients (if patient object has sourceTherapist)
      else if (patient?.sourceTherapist?.assignedPatients?.[patientId]?.appointments) {
        const appointments = patient?.sourceTherapist?.assignedPatients[patientId]?.appointments;
        if (appointments?.length > 0 && appointments[0]?.taskUuid) {
          taskUuidForUnprocessedData = appointments[0]?.taskUuid;
        }
      }
      // Priority 3: Fallback to therapistsList lookup
      else if (sourceTherapist && patientId && therapistsList) {
        const sourceTherapistData = therapistsList?.find((t: any) => t?.uuid === sourceTherapist);
        if (sourceTherapistData?.assignedPatients?.[patientId]?.appointments) {
          const appointments = sourceTherapistData?.assignedPatients[patientId]?.appointments;
          if (appointments?.length > 0 && appointments[0]?.taskUuid) {
            taskUuidForUnprocessedData = appointments[0]?.taskUuid;
          }
        }
      }

      // For migrate endpoint, we need taskId
      const taskIdForMigration = taskUuidForUnprocessedData || patientUuid;

      // Get therapist discipline for refresh
      const therapist = therapists?.find((t) => t.uuid === selectedTherapistUuid);
      const discipline =
        therapist?.discipline || selectedTherapist?.discipline || currentTab || 'PT';

      // Construct meta object matching drag-and-drop format for proper refresh
      const meta = {
        providerId: providerId,
        date: assignDate,
        discipline: discipline,
        sortBy: selectedSortType || '',
        type: '',
        active: true,
        skill: '',
        taskPage: 0,
        therapistPage: 0,
      };

      // Calculate source and destination pages (same logic as drag-and-drop)
      let sourcePage = 0;
      let destinationPage = 0;
      if (therapistsList && therapistsList?.length > 0) {
        sourcePage = getTherapistPageNumber(therapistsList, sourceTherapist);
        destinationPage = getTherapistPageNumber(therapistsList, selectedTherapistUuid);
      }

      lastMigrateRef.current = {
        providerId: providerId,
        taskUuid: Array.isArray(taskIdForMigration) ? taskIdForMigration[0] : taskIdForMigration,
        time: Math.max(1, Number(numberOfMinutes)) || 30,
      };

      const migratePayload = {
        providerId: providerId,
        sourceTherapist: sourceTherapist,
        targetTherapist: selectedTherapistUuid,
        taskId: taskIdForMigration,
        assignDate: assignDate,
        appointmentStartTime: appointmentStartTime,
        appointmentEndTime: appointmentEndTime,
        currentDate: assignDate,
        singleAssign: true,
        newDurationMinutes: Math.max(1, Number(numberOfMinutes)) || 30,
        meta: meta,
        sourcePage: sourcePage,
        destinationPage: destinationPage,
        filterByUuid:
          filterTherapistList && filterTherapistList?.length > 0 ? filterTherapistList : null,
        unprocessedData: [taskUuidForUnprocessedData],
      };
      lastMigratePayloadRef.current = migratePayload;
      dispatch(therapistMigrateTaskAction(migratePayload));
    } else {
      // Use verify endpoint when creating new appointment
      // Convert date from YYYY-MM-DD to DD-MM-YYYY for query parameter (matching drag-and-drop format)
      const assignDate = moment(localDate, 'YYYY-MM-DD').format('DD-MM-YYYY');

      // Get therapist discipline
      const therapist = therapists.find((t) => t.uuid === selectedTherapistUuid);
      const discipline = therapist?.discipline || selectedTherapist?.discipline || 'PT';

      lastMigrateRef.current = {
        providerId: providerId,
        taskUuid: patient?.taskUuid || patient?.uuid || patient?.patientUuid,
        time: Number(numberOfMinutes),
      };

      const schedulePayload = {
        providerId: providerId,
        therapistUuid: selectedTherapistUuid,
        patientUuid: patient.uuid || patient?.patientUuid,
        assignDate: assignDate,
        discipline: discipline,
        appointmentStartTime: appointmentStartTime,
        appointmentEndTime: appointmentEndTime,
        newDurationMinutes: Number(numberOfMinutes),
        isAppointmentBook: isAppointmentBook,
      };
      lastScheduleRef.current = schedulePayload;
      dispatch(scheduleAppointmentAction(schedulePayload));
    }
  };

  const   handleConfirmSchedule = () => {
    if (selectedTherapistUuid && selectedTimeSlot && patient && providerId) {
      // If isRecurringEdit is true, call the update recurring series API
      if (isRecurringEdit && patient?.recurringAppointmentUuid && isRecurringAppointmentBook) {
        // Parse the selected time
        const time24 = moment(selectedTimeSlot, 'h:mm A').format('HH:mm:ss');

        // Create appointment start time in UTC - use date from localDate, time from selectedTimeSlot
        const appointmentStartTime = moment
          .utc(`${localDate} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');

        // Calculate appointment end time based on duration
        const duration = Number(numberOfMinutes) || 30;
        const appointmentEndTime = moment
          .utc(`${localDate} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
          .add(duration, 'minutes')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');

        const payload = {
          recurringAppointmentUuid: patient.recurringAppointmentUuid,
          startDate: localDate,
          appointmentStartTime: appointmentStartTime,
          appointmentEndTime: appointmentEndTime,
          newDurationMinutes: duration,
        };

        dispatch(updateRecurringSeriesAction(payload));
        return;
      }

      // If recurring is selected, store the appointment and show popup instead of calling API
      if (isRecurring && recurrenceEndDate && isRecurringAppointmentBook) {
        // Use selectedRecurringWeekday if set (from popup), otherwise use today's day
        const selectedWeekday =
          selectedRecurringWeekday !== null ? selectedRecurringWeekday : moment().day();
        const dayName = moment().day(selectedWeekday).format('dddd');

        // Store the original end date on first appointment (if not already stored)
        if (scheduledRecurringAppointments?.length === 0 && !originalRecurrenceEndDate) {
          setOriginalRecurrenceEndDate(recurrenceEndDate);
        }

        // Check if an appointment for this weekday already exists
        const existingAppointmentIndex = scheduledRecurringAppointments.findIndex(
          (apt) => apt.weekday === selectedWeekday
        );

        let updatedAppointments;
        if (existingAppointmentIndex !== -1) {
          // Update existing appointment with new time
          updatedAppointments = [...scheduledRecurringAppointments];
          updatedAppointments[existingAppointmentIndex] = {
            weekday: selectedWeekday,
            dayName: dayName,
            time: selectedTimeSlot,
          };
        } else {
          // Add new appointment
          const newAppointment = {
            weekday: selectedWeekday,
            dayName: dayName,
            time: selectedTimeSlot,
          };
          updatedAppointments = [...scheduledRecurringAppointments, newAppointment];
        }

        setScheduledRecurringAppointments(updatedAppointments);
        // Update selectedRecurringDays from scheduled appointments
        const updatedDays = updatedAppointments.map((apt) => apt.weekday);
        setSelectedRecurringDays([...new Set(updatedDays)]); // Remove duplicates

        // Show popup
        setShowRecurringDayPopup(true);
        setIsFirstRecurringSchedule(true);
        setSelectedTimeSlot(null);
        return;
      }

      scheduleAppointment();
    }
  };

  const handleCancel = () => {
    setSelectedTherapistUuid('');
    setSelectedTimeSlot(null);
    dispatch(getAppointmentSlotsResetAction());
    dispatch(scheduleAppointmentResetAction());
    dispatch(scheduleRecurringAppointmentResetAction());
    dispatch(updateRecurringSeriesResetAction());
    dispatch(therapistMigrateTaskResetAction());
    dispatch(getPatientTimeSlotsResetAction());
    dispatch(getRecurringAppointmentCountResetAction());
    setShowRecurringDayPopup(false);
    setSelectedRecurringDays([]);
    setScheduledRecurringAppointments([]);
    setIsFirstRecurringSchedule(true);
    setIsDoneSchedulingClicked(false);
    setOriginalRecurrenceEndDate(null);
    setSelectedRecurringWeekday(null);
    onClose();
  };

  const handleSelectRecurringDay = (weekday: number) => {
    setSelectedRecurringWeekday(weekday);

    setShowRecurringDayPopup(false);
    setSelectedTimeSlot(null);
  };

  const handleRemoveRecurringAppointment = (indexToRemove: number) => {
    const updatedAppointments = scheduledRecurringAppointments.filter(
      (_, index) => index !== indexToRemove
    );
    setScheduledRecurringAppointments(updatedAppointments);

    // Update selectedRecurringDays based on remaining appointments
    const updatedDays = updatedAppointments.map((apt) => apt.weekday);
    setSelectedRecurringDays([...new Set(updatedDays)]);
  };

  const handleCloseRecurringPopup = () => {
    setShowRecurringDayPopup(false);
  };

  const handleDoneScheduling = () => {
    const endDateToUse = recurrenceEndDate || originalRecurrenceEndDate;

    if (
      scheduledRecurringAppointments?.length > 0 &&
      selectedTherapistUuid &&
      patient &&
      providerId &&
      endDateToUse &&
      localDate
    ) {
      setIsDoneSchedulingClicked(true);

      const therapist = therapists.find((t) => t.uuid === selectedTherapistUuid);
      const discipline = therapist?.discipline || selectedTherapist?.discipline || 'PT';

      const patientId = patient?.patientUuid || patient?.uuid;

      // Convert dates
      const startDate = moment(localDate, 'YYYY-MM-DD').format('YYYY-MM-DD');
      const endDate = moment(endDateToUse).format('YYYY-MM-DD');

      // Convert scheduledRecurringAppointments to schedules format
      const schedules = scheduledRecurringAppointments.map((appointment) => {
        // Convert weekday number to day name (uppercase)
        const dayNames = [
          'SUNDAY',
          'MONDAY',
          'TUESDAY',
          'WEDNESDAY',
          'THURSDAY',
          'FRIDAY',
          'SATURDAY',
        ];
        const dayOfWeek = dayNames[appointment.weekday];

        // Find the first occurrence of this weekday from startDate
        const startMoment = moment(localDate, 'YYYY-MM-DD');
        let firstOccurrence = startMoment.clone();

        // Calculate days until the target weekday
        const currentWeekday = startMoment.day();
        const targetWeekday = appointment.weekday;
        let daysUntilTarget = targetWeekday - currentWeekday;
        if (daysUntilTarget < 0) {
          daysUntilTarget += 7; // Next week
        }
        firstOccurrence.add(daysUntilTarget, 'days');

        // Parse the time (e.g., "7:00 AM")
        const time24 = moment(appointment.time, 'h:mm A').format('HH:mm:ss');

        // Create appointment start time in UTC
        const appointmentStartTime = moment
          .utc(`${firstOccurrence.format('YYYY-MM-DD')} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');

        // Calculate appointment end time based on duration
        const duration = Number(numberOfMinutes) || 30;
        const appointmentEndTime = moment
          .utc(`${firstOccurrence.format('YYYY-MM-DD')} ${time24}`, 'YYYY-MM-DD HH:mm:ss')
          .add(duration, 'minutes')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');

        return {
          dayOfWeek,
          appointmentStartTime,
          appointmentEndTime,
        };
      });

      const payload = {
        patientId,
        therapistUuid: selectedTherapistUuid,
        discipline,
        tasksType: patient?.tasksType || 'EVALUATION',
        minutes: Number(numberOfMinutes) || 30,
        startDate,
        endDate,
        schedules,
      };

      dispatch(scheduleRecurringAppointmentAction(payload));
    } else {
      // If no appointments scheduled, just close
      setShowRecurringDayPopup(false);
      handleCancel();
    }
  };

  const isMigrating = migrateTaskState?.loading || false;
  const isScheduling =
    scheduleAppointmentState?.loading ||
    scheduleRecurringAppointmentState?.loading ||
    updateRecurringSeriesState?.loading ||
    false;
  const showLoader = isMigrating || isScheduling;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '16px',
          position: 'relative',
        },
      }}
    >
      {/* Loader overlay when migrate or schedule API is loading */}
      {showLoader && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            borderRadius: '12px',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
          p: 0,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl
                size="small"
                sx={{
                  minWidth: 200,
                  border: '2px solid #969696 !important',
                  borderRadius: '8px !important',
                }}
              >
                <Select
                  value={selectedTherapistUuid}
                  onChange={handleTherapistChange}
                  displayEmpty
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 500,
                    '.MuiSelect-select': {
                      fontSize: '16px !important',
                    },
                  }}
                  disabled={
                    isRecurringEdit ||
                    (isRecurring &&
                      scheduledRecurringAppointments?.length >= 1 &&
                      selectedRecurringWeekday !== null &&
                      !scheduledRecurringAppointments?.some(
                        (apt) => apt?.weekday === selectedRecurringWeekday
                      ))
                  }
                >
                  {therapists?.map((therapist) => (
                    <MenuItem
                      key={therapist?.uuid}
                      value={therapist?.uuid}
                    >
                      {therapist?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="h6"
                fontWeight={600}
              >
                Appointments | {moment(localDate, 'YYYY-MM-DD').format('MMM. DD')}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCancel}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {patient && (
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: '14px', color: '#595d64', fontWeight: 600 }}
              >
                Select appointment time for
              </Typography>
              <Chip
                variant="outlined"
                size="small"
                label={
                  patient.patientLastname
                    ? `${patient?.patientLastname}, ${patient?.patientFirstname}`
                    : patient?.name
                }
                sx={{
                  color: '#111927',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  width: 'fit-content',
                  maxWidth: '150px',
                  height: '22px',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  borderRadius: '20px',
                  background: '#FFF',
                  borderTop: '1px solid #b9aeae !important',
                  borderLeft: '1px solid #b9aeae !important',
                  borderRight: '1px solid #b9aeae !important',
                  borderBottom: (() => {
                    const payerGroup = patient?.payerPlanType
                      ? getPayerCateGroup(patient?.payerPlanType)
                      : null;
                    const borderColor = payerGroup?.borderColor || '#E5E7EB';
                    return `2px solid ${borderColor} !important`;
                  })(),
                  '& .MuiChip-label': {
                    padding: '0 12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
              <TextField
                type="number"
                size="small"
                value={numberOfMinutes}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    const newMinutes = value === '' ? '' : Number(value);
                    setNumberOfMinutes(newMinutes);
                  }
                }}
                sx={{
                  width: '80px',
                  '& .MuiInputBase-root': {
                    fontSize: '13px',
                    height: '28px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 8px',
                    textAlign: 'center',
                  },
                }}
                inputProps={{
                  min: 1,
                  style: { textAlign: 'center' },
                }}
                disabled={isRecurringEdit || !!patient?.splitRef}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: '14px', color: '#595d64', fontWeight: 600 }}
              >
                Minutes
              </Typography>
            </Box>
          )}

          {/* Recurring Appointment Section */}
          {patient &&
            !patient?.splitRef &&
            !migrateRecurring &&
            isRecurringAppointmentBook &&
            (isRecurringEdit === undefined || isRecurringEdit === true) && (
              <Box
                sx={{
                  mt: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'nowrap',
                  border: '1px solid #cacaca',
                  padding: 1,
                  borderRadius: 1,
                  background: '#e8f1fa',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isRecurring}
                      disabled={isRecurringEdit}
                      onChange={(e) => {
                        if (!isRecurringEdit) {
                          setIsRecurring(e.target.checked);
                          if (e.target.checked && selectedRecurringWeekday === null) {
                            // Initialize to today's day when recurring is first enabled
                            setSelectedRecurringWeekday(moment().day());
                          } else if (!e.target.checked) {
                            setRecurrenceEndDate(null);
                            setSelectedRecurringWeekday(null);
                            setScheduledRecurringAppointments([]);
                          }
                        }
                      }}
                      sx={{
                        color: '#111927',
                        '&.Mui-checked': {
                          color: '#3B82F6',
                        },
                        '&.Mui-disabled': {
                          color: '#b8bdc3',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <RepeatRoundedIcon
                        sx={{ fontSize: '22px', color: '#111927', flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          color: '#111927',
                          fontWeight: 500,
                          wordBreak: 'break-word',
                          maxWidth: '250px',
                        }}
                      >
                        Make this a recurring appointment
                      </Typography>
                    </Box>
                  }
                  sx={{ flexShrink: 1, minWidth: 0 }}
                />
                {isRecurring && (
                  <>
                    <Box
                      sx={{
                        width: '1px',
                        height: '26px',
                        backgroundColor: '#b5adad',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: '#595d64',
                        fontWeight: 600,
                        flexShrink: 1,
                        minWidth: 0,
                        maxWidth: '150px',
                        wordBreak: 'break-word',
                        display: 'flex',
                      }}
                    >
                      Every{' '}
                      {selectedRecurringWeekday !== null
                        ? moment().day(selectedRecurringWeekday).format('dddd')
                        : moment().format('dddd')}{' '}
                      until*
                    </Typography>
                    <DatePicker
                      value={recurrenceEndDate}
                      onChange={(newDate) => {
                        if (!isRecurringEdit) {
                          setRecurrenceEndDate(newDate);
                        }
                      }}
                      disabled={isRecurringEdit}
                      minDate={moment().toDate()}
                      maxDate={
                        isRecurring && patient?.latestCertEndDate
                          ? moment(patient?.latestCertEndDate).toDate()
                          : undefined
                      }
                      slots={{
                        textField: TextField,
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'Select end date',
                          error: false,
                          sx: {
                            width: '180px',
                            minWidth: '180px',
                            backgroundColor: '#FFFFFF',
                            flexShrink: 0,
                            borderRadius: '8px',
                            '& .MuiInputBase-root': {
                              fontSize: '14px',
                              border: recurrenceEndDate ? '1px solid #E5E7EB' : '1px solid #F87171',
                              borderRadius: '8px',
                              backgroundColor: '#FFFFFF',
                              color: '#111927',
                              '&:hover': {
                                borderColor: recurrenceEndDate ? '#D1D5DB' : '#F87171',
                              },
                              '&.Mui-focused': {
                                borderColor: recurrenceEndDate ? '#3B82F6' : '#F87171',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '8px 12px',
                              cursor:
                                scheduledRecurringAppointments.length > 0
                                  ? 'not-allowed'
                                  : 'pointer',
                              color: recurrenceEndDate ? '#111927' : '#F87171',
                              '&::placeholder': {
                                color: '#F87171',
                                opacity: 1,
                              },
                            },
                            '& .MuiInputAdornment-root': {
                              '& .MuiIconButton-root': {
                                color: recurrenceEndDate ? '#6C737F' : '#F87171',
                              },
                            },
                          },
                        },
                      }}
                      format="MM/dd/yyyy"
                    />
                    {recurrenceEndDate && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backgroundColor: '#E0E7FF',
                          borderRadius: '20px',
                          padding: '6px 12px',
                          flexShrink: 1,
                          minWidth: 0,
                          maxWidth: '250px',
                        }}
                      >
                        <CalendarTodayIcon
                          sx={{
                            fontSize: '16px',
                            color: '#6366F1',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '14px',
                            color: '#6366F1',
                            fontWeight: 500,
                            wordBreak: 'break-word',
                          }}
                        >
                          {(() => {
                            // Show API count if available and time slot is selected, otherwise show manual calculation
                            if (
                              !localDate ||
                              !recurrenceEndDate ||
                              selectedRecurringWeekday === null
                            )
                              return '';

                            // If time slot is selected and API has returned a count, use it
                            if (
                              selectedTimeSlot &&
                              recurringAppointmentCountState?.success &&
                              recurringAppointmentCountState?.response?.data?.availableCount !==
                                undefined
                            ) {
                              const count =
                                recurringAppointmentCountState?.response?.data?.availableCount;
                              return `${count} appointment${
                                count !== 1 ? 's' : ''
                              } will be created`;
                            }

                            // Otherwise, calculate manually (fallback for when date is selected but time is not)
                            const startDate = moment(localDate, 'YYYY-MM-DD');
                            const endDate = moment(recurrenceEndDate);
                            const selectedWeekday = selectedRecurringWeekday;
                            let count = 0;
                            let currentDate = startDate.clone();

                            // Find the first occurrence of the selected weekday on or after the start date
                            while (currentDate.day() !== selectedWeekday) {
                              currentDate.add(1, 'day');
                            }

                            // Count occurrences of the selected weekday until end date
                            while (currentDate.isSameOrBefore(endDate)) {
                              count++;
                              currentDate.add(7, 'days');
                            }

                            return `${count} appointment${count !== 1 ? 's' : ''} will be created`;
                          })()}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          '::-webkit-scrollbar': {
            width: '12px !important',
          },
        }}
      >
        {/* {assignedInpatients?.length > 0 && ( */}
        <Box sx={{ mb: 2, pt: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '13px', color: '#111927' }}
          >
            <strong>Assigned Inpatients:</strong>{' '}
            {assignedInpatients?.length > 0 ? assignedInpatients.join(', ') : 'None'}
          </Typography>
        </Box>

        {/* {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
            }}
          >
            <img
              src={LoaderIcon}
              alt="Loading..."
            />
          </Box>
        ) : ( */}
        <TableContainer
          sx={{
            border: '1px solid #cacaca',
            borderRadius: '8px !important',
          }}
        >
          <Table
            size="small"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': {
                borderBottom: 'none',
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ borderBottom: '1px solid #cacaca' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '100px',
                    fontSize: '13px',
                    py: 1.5,
                    background: '#e8f1fa !important',
                    textTransform: 'capitalize !important',
                    borderRight: '1px solid #cacaca',
                  }}
                >
                  Time
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '50%',
                    fontSize: '13px',
                    py: 1.5,
                    background: '#e8f1fa !important',
                    textTransform: 'capitalize !important',
                  }}
                >
                  Patient
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '50%',
                    fontSize: '13px',
                    color: '#111927',
                    py: 1.5,
                    background: '#e8f1fa !important',
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedSlots?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                  >
                    No appointment slots available
                  </TableCell>
                </TableRow>
              ) : (
                groupedSlots?.map((group, groupIndex) => {
                  // Split slots into pairs for two-column display
                  const rows = [];
                  // Alternate background color based on hour group (even = white, odd = light gray)
                  const backgroundColor = groupIndex % 2 === 0 ? '#FFFFFF' : '#cecaca1f';

                  for (let i = 0; i < group?.slots?.length; i += 2) {
                    const slot1 = group?.slots[i];
                    const slot2 = group?.slots[i + 1];
                    // Check if this is the last row of the group
                    const isLastRow = i + 2 >= group?.slots?.length;

                    rows.push(
                      <TableRow
                        key={`${groupIndex}-${i}`}
                        sx={{
                          backgroundColor: backgroundColor,
                        }}
                      >
                        {/* Hour Column - only show on first row of each group */}
                        {i === 0 ? (
                          <TableCell
                            rowSpan={Math.ceil(group?.slots?.length / 2)}
                            sx={{
                              fontWeight: 600,
                              verticalAlign: 'top',
                              color: '#111927',
                              fontSize: '14px',
                              borderRight: '2px solid #969696',
                              borderBottom: '2px solid #969696 !important',
                              width: '100px',
                              maxWidth: '100px',
                              backgroundColor: backgroundColor,
                            }}
                          >
                            {group?.hour}
                          </TableCell>
                        ) : null}

                        {/* First Slot Column */}
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            py: 1,
                            width: '50%',
                            alignContent: 'center',
                            backgroundColor: backgroundColor,
                            borderBottom: isLastRow ? '2px solid #969696 !important' : 'none',
                          }}
                        >
                          {slot1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  color: '#6C737F',
                                  minWidth: '70px',
                                  fontWeight: 600,
                                }}
                              >
                                {slot1?.time}
                              </Typography>
                              {slot1?.unavailable ? (
                                <Typography
                                  sx={{
                                    color: '#6C737F',
                                    fontSize: '13px',
                                    minWidth: '70px',
                                    fontWeight: 600,
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {slot1?.patient}
                                </Typography>
                              ) : slot1?.available ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleSelectTimeSlot(slot1.time)}
                                  sx={{
                                    textTransform: 'none',
                                    color: '#555a62',
                                    fontSize: '13px',
                                    padding: '4px 12px',
                                    minWidth: 'auto',
                                    borderColor: '#b6b6b6',
                                    borderRadius: '8px',
                                    backgroundColor: '#FFF',
                                    '&:hover': {
                                      backgroundColor: '#1119270a',
                                      borderColor: '#b6b6b6',
                                    },
                                  }}
                                >
                                  + Available
                                </Button>
                              ) : slot1?.temporary ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Chip
                                    variant="outlined"
                                    size="small"
                                    label={slot1?.patient}
                                    sx={{
                                      color: '#111927',
                                      fontFamily: 'Inter',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      width: 'fit-content',
                                      maxWidth: '150px',
                                      height: '22px',
                                      textAlign: 'left',
                                      textOverflow: 'ellipsis',
                                      borderRadius: '20px',
                                      background: '#FFF',
                                      borderTop: '1px solid #b9aeae !important',
                                      borderLeft: '1px solid #b9aeae !important',
                                      borderRight: '1px solid #b9aeae !important',
                                      borderBottom: (() => {
                                        const payerGroup = patient?.payerPlanType
                                          ? getPayerCateGroup(patient.payerPlanType)
                                          : null;
                                        const borderColor = payerGroup?.borderColor || '#E5E7EB';
                                        return `2px solid ${borderColor} !important`;
                                      })(),
                                      '& .MuiChip-label': {
                                        padding: '0 12px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      },
                                    }}
                                  />
                                  <Typography
                                    sx={{ fontSize: '13px', color: '#6C737F', fontWeight: 600 }}
                                  >
                                    {numberOfMinutes} Min.
                                  </Typography>
                                </Box>
                              ) : slot1?.isAppointment ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Chip
                                    variant="outlined"
                                    size="small"
                                    label={slot1?.patient}
                                    sx={{
                                      color: '#111927',
                                      fontFamily: 'Inter',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      width: 'fit-content',
                                      maxWidth: '150px',
                                      height: '22px',
                                      textAlign: 'left',
                                      textOverflow: 'ellipsis',
                                      borderRadius: '20px',
                                      background: '#FFF',
                                      borderTop: '1px solid #b9aeae !important',
                                      borderLeft: '1px solid #b9aeae !important',
                                      borderRight: '1px solid #b9aeae !important',
                                      borderBottom: (() => {
                                        const payerGroup = slot1?.appointmentData?.payerPlanType
                                          ? getPayerCateGroup(slot1?.appointmentData?.payerPlanType)
                                          : null;
                                        const borderColor = payerGroup?.borderColor || '#E5E7EB';
                                        return `2px solid ${borderColor} !important`;
                                      })(),
                                      '& .MuiChip-label': {
                                        padding: '0 12px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      },
                                    }}
                                  />
                                  {slot1?.duration && (
                                    <Typography
                                      sx={{ fontSize: '13px', color: '#6C737F', fontWeight: 600 }}
                                    >
                                      {slot1?.duration}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      color: '#111927',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {slot1?.patient}
                                  </Typography>
                                  {slot1?.duration && (
                                    <Typography sx={{ fontSize: '13px', color: '#6C737F' }}>
                                      {slot1?.duration}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </TableCell>

                        {/* Second Slot Column */}
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            py: 1,
                            width: '50%',
                            alignContent: 'center',
                            backgroundColor: backgroundColor,
                            borderBottom: isLastRow ? '2px solid #969696 !important' : 'none',
                          }}
                        >
                          {slot2 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  color: '#6C737F',
                                  minWidth: '70px',
                                  fontWeight: 600,
                                }}
                              >
                                {slot2?.time}
                              </Typography>
                              {slot2?.unavailable ? (
                                <Typography
                                  sx={{
                                    color: '#6C737F',
                                    fontSize: '13px',
                                    minWidth: '70px',
                                    fontWeight: 600,
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {slot2?.patient}
                                </Typography>
                              ) : slot2?.available ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleSelectTimeSlot(slot2?.time)}
                                  sx={{
                                    textTransform: 'none',
                                    color: '#555a62',
                                    fontSize: '13px',
                                    padding: '4px 12px',
                                    minWidth: 'auto',
                                    borderColor: '#b6b6b6',
                                    borderRadius: '8px',
                                    backgroundColor: '#FFF',
                                    display: 'flex',
                                    flex: 'none',
                                    '&:hover': {
                                      backgroundColor: '#1119270a',
                                      borderColor: '#b6b6b6',
                                    },
                                  }}
                                >
                                  + Available
                                </Button>
                              ) : slot2?.temporary ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Chip
                                    variant="outlined"
                                    size="small"
                                    label={slot2?.patient}
                                    sx={{
                                      color: '#111927',
                                      fontFamily: 'Inter',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      width: 'fit-content',
                                      maxWidth: '150px',
                                      height: '22px',
                                      textAlign: 'left',
                                      textOverflow: 'ellipsis',
                                      borderRadius: '20px',
                                      background: '#FFF',
                                      borderTop: '1px solid #b9aeae !important',
                                      borderLeft: '1px solid #b9aeae !important',
                                      borderRight: '1px solid #b9aeae !important',
                                      borderBottom: (() => {
                                        const payerGroup = patient?.payerPlanType
                                          ? getPayerCateGroup(patient.payerPlanType)
                                          : null;
                                        const borderColor = payerGroup?.borderColor || '#E5E7EB';
                                        return `2px solid ${borderColor} !important`;
                                      })(),
                                      '& .MuiChip-label': {
                                        padding: '0 12px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      },
                                    }}
                                  />
                                  <Typography
                                    sx={{ fontSize: '13px', color: '#6C737F', fontWeight: 600 }}
                                  >
                                    {slot2?.duration}
                                  </Typography>
                                </Box>
                              ) : slot2?.isAppointment ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Chip
                                    variant="outlined"
                                    size="small"
                                    label={slot2?.patient}
                                    sx={{
                                      color: '#111927',
                                      fontFamily: 'Inter',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      width: 'fit-content',
                                      maxWidth: '150px',
                                      height: '22px',
                                      textAlign: 'left',
                                      textOverflow: 'ellipsis',
                                      borderRadius: '20px',
                                      background: '#FFF',
                                      borderTop: '1px solid #b9aeae !important',
                                      borderLeft: '1px solid #b9aeae !important',
                                      borderRight: '1px solid #b9aeae !important',
                                      borderBottom: (() => {
                                        const payerGroup = slot2?.appointmentData?.payerPlanType
                                          ? getPayerCateGroup(slot2?.appointmentData.payerPlanType)
                                          : null;
                                        const borderColor = payerGroup?.borderColor || '#E5E7EB';
                                        return `2px solid ${borderColor} !important`;
                                      })(),
                                      '& .MuiChip-label': {
                                        padding: '0 12px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      },
                                    }}
                                  />
                                  {slot2?.duration && (
                                    <Typography
                                      sx={{ fontSize: '13px', color: '#6C737F', fontWeight: 600 }}
                                    >
                                      {slot2?.duration}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      color: '#111927',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {slot2?.patient}
                                  </Typography>
                                  {slot2?.duration && (
                                    <Typography
                                      sx={{ fontSize: '13px', color: '#6C737F', fontWeight: 600 }}
                                    >
                                      {slot2?.duration}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return rows;
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 3, pb: 0, justifyContent: 'end' }}>
        <Button
          onClick={handleCancel}
          variant="text"
          sx={{
            textTransform: 'none',
            color: '#6C737F',
            fontSize: '14px',
            border: '0.5px solid',
            '&:hover': {
              backgroundColor: '#cecaca1f',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            !selectedTimeSlot ||
            scheduleAppointmentState?.loading ||
            updateRecurringSeriesState?.loading ||
            migrateTaskState?.loading ||
            (isRecurring && !recurrenceEndDate)
          }
          onClick={handleConfirmSchedule}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            backgroundColor:
              selectedTimeSlot && (!isRecurring || recurrenceEndDate) ? '#3B82F6' : '#E5E7EB',
            color: selectedTimeSlot && (!isRecurring || recurrenceEndDate) ? '#FFFFFF' : '#9CA3AF',
            '&:hover': {
              backgroundColor:
                selectedTimeSlot && (!isRecurring || recurrenceEndDate) ? '#2563EB' : '#E5E7EB',
            },
            '&.Mui-disabled': {
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF',
            },
          }}
        >
          Schedule Appointment
        </Button>
      </DialogActions>

      {/* Recurring Day Selection Popup */}
      <Dialog
        open={showRecurringDayPopup}
        onClose={scheduleRecurringAppointmentState?.loading ? undefined : handleCloseRecurringPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            position: 'relative',
          },
        }}
      >
        {scheduleRecurringAppointmentState?.loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: '12px',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 2,
            borderBottom: '1px solid #cacaca',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RepeatRoundedIcon sx={{ color: '#FFFFFF', fontSize: '24px' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600 }}
            >
              Recurring Appointment Scheduled
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#6C737F' }}
            >
              {scheduledRecurringAppointments?.length} recurring session
              {scheduledRecurringAppointments?.length !== 1 ? 's' : ''} scheduled for{' '}
              {patient?.patientLastname
                ? `${patient?.patientLastname}, ${patient?.patientFirstname}`
                : patient?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3, pt: 1 }}>
            <Typography
              variant="paragraphHeading"
              sx={{ mb: 1, fontWeight: 600, color: '#6C737F' }}
            >
              SCHEDULED
            </Typography>
            <Box>
              {scheduledRecurringAppointments?.length > 0 ? (
                scheduledRecurringAppointments?.map((appointment, index) => (
                  <Chip
                    key={index}
                    icon={<CalendarMonthOutlinedIcon style={{ color: '#3a3ee7' }} />}
                    label={`${appointment?.dayName}s at ${appointment?.time}`}
                    onDelete={
                      scheduledRecurringAppointments?.length > 1
                        ? () => handleRemoveRecurringAppointment(index)
                        : undefined
                    }
                    deleteIcon={
                      scheduledRecurringAppointments?.length > 1 ? (
                        <CloseIcon
                          sx={{
                            fontSize: '16px !important',
                            color: '#3a3ee7',
                            '&:hover': {
                              color: '#1e40af',
                            },
                          }}
                        />
                      ) : undefined
                    }
                    sx={{
                      backgroundColor: '#E0E7FF',
                      color: '#3a3ee7',
                      fontWeight: 600,
                      mb: 1,
                      mr: 1,
                      width: 'min-content',
                    }}
                  />
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: '#9CA3AF', fontStyle: 'italic' }}
                >
                  No appointments scheduled yet
                </Typography>
              )}
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{ mb: 2, color: '#111927' }}
          >
            Would you like to schedule another recurring session on a different day?
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[0, 1, 2, 3, 4, 5, 6].map((weekday) => {
              const dayName = moment().day(weekday).format('ddd');
              const isScheduled = scheduledRecurringAppointments?.some(
                (apt) => apt.weekday === weekday
              );

              return (
                <Button
                  key={weekday}
                  variant="outlined"
                  onClick={() => handleSelectRecurringDay(weekday)}
                  disabled={isScheduled}
                  sx={{
                    minWidth: '60px',
                    textTransform: 'none',
                    borderColor: isScheduled ? '#E5E7EB' : '#D1D5DB',
                    color: isScheduled ? '#9CA3AF' : '#111927',
                    backgroundColor: isScheduled ? '#F9FAFB' : '#FFFFFF',
                    '&:hover': {
                      borderColor: isScheduled ? '#E5E7EB' : '#3B82F6',
                      backgroundColor: isScheduled ? '#F9FAFB' : '#F3F4F6',
                    },
                    display: isScheduled ? 'none' : '',
                  }}
                >
                  {dayName}
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleDoneScheduling}
            disabled={scheduleRecurringAppointmentState?.loading}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              backgroundColor: '#6366F1',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#4F46E5',
              },
              '&:disabled': {
                backgroundColor: '#9CA3AF',
                color: '#FFFFFF',
              },
            }}
          >
            Done Scheduling
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
    <WarningModal
      open={overlapModalOpen}
      title="Overlapping Appointment"
      message="This change will create an overlap with another appointment for this patient. Continue?"
      buttonText="Confirm"
      cancelBtn="Cancel"
      primaryCTA
      onClose={handleOverlapCancel}
      handleSaveBtn={handleOverlapConfirm}
    />
    </>
  );
};

export default AppointmentSchedulingModal;
